export const useLeadGenData = () => {
  const { logError } = useErrorLogger()
  
  const transform = (response) => {
    const { sanitize } = useUtils()

    return {
      leadGenFields: sanitize(response?.data?.attributes?.leadGenFields)
    }
  }

  const transformFullWidth = (response) => {
    const { sanitize } = useUtils()

    return sanitize(response.data.attributes)
  }

  const transformRibbonForm = (response) => {
    const { sanitize } = useUtils()

    return {
      ribbonForm: sanitize(response?.data?.attributes?.ribbonForm),
    }
  }

  const transformBrochureRequest = (response) => {
    const { sanitize } = useUtils()

    return {
      ...response.data.attributes,
      heroHeader: sanitize(response?.data?.attributes?.heroHeader),
    }
  }

  const leadGenData = async (_deep = true) => {
    const url = `/api/lead-gen`;
    
    return await useFetch(url, {
      method: 'GET',
      transform: transform
    });
  }

  const leadGenFullWidthData = async (_deep = true) => {
    const url = `/api/lead-gen-full-width`;
    
    return await useFetch(url, {
      method: 'GET',
      transform: transformFullWidth
    });
  }

  const ribbonFormData = async (_deep = true) => {
    const url = `/api/brochure-request-ribbon-lead`;

    return await useFetch(url, {
      method: 'GET',
      transform: transformRibbonForm
    });
  }

  const getLeadGenLocal = async () => {
    return await useFetch('/api/lead-gen')
  }

  const getBrochureRequestPage = async () => {
    return await useFetch('/api/brochure-request-page', {
      method: 'GET',
      transform: transformBrochureRequest,
    })
  }

  const getBrochurePage = async () => {
    return await useFetch('/api/brochure-page', {
      method: 'GET',
      transform: transformBrochureRequest,
    })
  }

  const setLeadGenLocal = async (data) => {
    return await useFetch('/api/lead-gen', {
      method: 'post',
      body: data,
    })
  }

  const sendLeadGenRequest = async (data) => {
    return await useFetch('/api/submit-lead-gen', {
      method: 'post',
      body: data,
    })
  }

  const getCountries = async () => {
    return await useFetch('/api/countries')
  }

  const sanitizeLeadGenPayload = (formData) => {
    const data = {
      leadTypeID: formData.leadTypeID.value,
      firstName: formData.firstName.value,
      emailAddress: formData.emailAddress.value
    }

    if (formData.postalCode) {
      data.postalCode = formData.postalCode.value;
    }

    if (formData.learningStyleTypeIDs) {
      if (formData.learningStyleTypeIDs.value.length > 0) {
        data.learningStyleTypeIDs = formData.learningStyleTypeIDs.value;
      }
      else {
        data.learningStyleTypeIDs = []
      }
    }

    if (formData.locationName) {
      data.locationName = formData.locationName;
    }
    
    // Only applies for the brochure request page
    if (formData.brochure) {
      data.sendPrintedInformation = formData.brochure.value ? formData.brochure.value.brochureSendPrintedInformation.value : false;
      if (data.sendPrintedInformation) {
        data.firstName = formData.brochure.value.brochureFirstName.value;
        data.lastName = formData.brochure.value.brochureLastName.value;
        data.phoneNumber = { 'countryCode': '', 'areaCode': '', 'number': formData.brochure.value.brochurePhoneNumber.value, 'extension': '' };
        data.address1 = formData.brochure.value.brochureAddress1.value;
        data.address2 = formData.brochure.value.brochureAddress2.value;
        data.city = formData.brochure.value.brochureCity.value;
        data.state = formData.brochure.value.brochureState.value;
        data.postalCode = formData.brochure.value.brochurePostalCode.value;
        data.country = formData.brochure.value.brochureCountry.value;
      }
      // Add studentBirthYears logic
      if (Array.isArray(formData.studentBirthYears.value) 
          && formData.studentBirthYears.value.length > 0 
          && formData.studentBirthYears.value[0] !== "") {
        data.students = formData.studentBirthYears.value.map(year => ({
          studentFirstName: "",
          studentBirthYear: parseInt(year, 10),
        }));
      } else {
        data.students = []; // Initialize as an empty array if studentBirthYears is not valid
      }
    }
    // Adjust phone number
    if (formData.phoneNumber && formData.phoneNumber.value) {
      data.phoneNumber = { 'countryCode': '', 'areaCode': '', 'number': formData.phoneNumber.value, 'extension': '' }
    }

    return data;
  }

  const getLocationShortFormFields = () => {
    return {
      leadTypeID: {
        value: 67008,
        valid: true 
      },
      firstName: {     
        value: '',
        required: true,
        valid: false
      },
      emailAddress: {
        value: '',
        required: true,
        valid: false
      },
      locationName: {
        value: '',
        required: false,
        valid: true
      }
    }
  }

  const getBrochureRequestFormFields = () => {
    
    return {
      leadTypeID: {
        value: 67000,
        valid: true 
      },
      firstName: {     
        value: '',
        required: true,
        valid: false
      },
      emailAddress: {
        value: '',
        required: true,
        valid: false
      },
      postalCode: {        
        value: '',
        required: false,
        valid: true
      },
      studentBirthYears: {        
        value: '',
        required: false,
        valid: true
      },
      learningStyleTypeIDs: {        
        required: false,
        valid: true,        
        value: []
      },
      phoneNumber: {            
        value: '',
        valid: true,
        required: false,
      },   
      brochure: {    
        required: true,
        valid: true,
        fields: {
          sendPrintedInformation: {
            value: false,
            valid: false
          },
          firstName: {            
            value: '',
            required: true,
            valid: false
          },
          lastName: {            
            value: '',
            required: true,
            valid: false
          },
          address1: {            
            value: '',
            required: true,
            valid: false
          },
          address2: {            
            value: '',
            required: false,
            valid: true
          },
          city: {            
            value: '',
            required: true,
            valid: false
          },
          state: {           
            value: '',
            required: true,
            valid: false
          },
          postalCode: {            
            value: '',
            required: true,
            valid: false
          },
          country: {           
            value: '',
            required: false,
            valid: true
          },
          phoneNumber: {            
            value: '',
            required: false,
            valid: true
          }    
        }
      }
    }
  }

  // Validating if unsanctionated characters are not present
  const checkInputFields = (inputField) => {
    const invalidChars = /[<>"!#$”]/;  
    return invalidChars.test(inputField);
  }

  const checkPulseUser = async (emailAddress) => {
    return await useFetch('/api/check-user', {
      method: 'POST',
      body: { emailAddress: emailAddress }
    })
  }

  const getFullWidthRibbonFormFields = () => {
    return {
      leadTypeID: {
        value: 67013,
        valid: true 
      },
      firstName: {     
        value: '',
        required: true,
        valid: false
      },
      emailAddress: {
        value: '',
        required: true,
        valid: false
      },
      phoneNumber: {
        value: '',
        required: false, // set from CMS at runtime when visible + mandatory
        valid: true      // true when optional/hidden; false when mandatory and empty
      }
    }
  }

  const getSharedFullWidthRibbon = async () => {
    return await useFetch('/api/lead-full-width-ribbon')
  }

  const submitLead = async (json) => {
    return await useFetch('/api/submit-lead', {
      method: 'POST',
      body: json
    })
  }

  const validateWithZeroBounce = async (email) => {
  const config = useRuntimeConfig()
  const encodedEmail = encodeURIComponent(email)
  const url = `https://api.zerobounce.net/v2/validate?api_key=${config.public.zeroBounceAPIKey}&email=${encodedEmail}&ip_address=`
  return await useFetch(url)
}

  const processLeadSubmission = async (formObject) => {
    try {
      // Read cookies before the first await. A cookie failure must not block the lead.
      let payload = formObject
      try {
        payload = applyUtmAttribution(formObject)
      } catch (err) {
        logError(err, { context: 'UTM attribution before lead submit' })
      }

      const brochureRequestStore = useBrochureRequest();
      // ✅ First API: check user existence
      const {data: userExistResponse } = await checkPulseUser(formObject.emailAddress)
      brochureRequestStore.setIsUser(userExistResponse?.value?.isUser)
      brochureRequestStore.setIsOptedOutEmail(userExistResponse?.value?.isOptedOutEmail)
      brochureRequestStore.setIsOptedOutSMS(userExistResponse?.value?.isOptedOutSMS)      

      const formJSON = JSON.stringify(payload)
      const {data: apiResponse } = await submitLead(formJSON)

      // Check if apiResponse is truthy (i.e., it's not null, undefined, or an empty object)
      const submissionResponse = {
        status: 200,
        message: apiResponse?.value?.message || 'An error has occurred.'
      }

      if (apiResponse?.value?.message == 'The request is invalid.' || apiResponse?.value?.message == 'An error has occurred.') {
        submissionResponse.status = 500;
      } else {
        brochureRequestStore.update(formObject);
        /** Embedded sync: `watch(hasIterableIdentity)` in iterable.client (avoids duplicate sync with explicit call). */
        const iterable = useIterable();
        if (iterable?.setEmail && formObject?.emailAddress) {
          iterable.setEmail(formObject.emailAddress).catch((err) => {
            logError(err, { context: 'Iterable setEmail after lead gen' });
          });
        }
      }

      return submissionResponse;

    } catch (err) {
      console.error('An error occured while trying to process lead submission:', err)
    }
    
  }

  return {
    leadGenData,
    ribbonFormData,
    getLeadGenLocal,
    setLeadGenLocal,    
    getBrochureRequestFormFields,
    sanitizeLeadGenPayload, 
    sendLeadGenRequest,
    getBrochureRequestPage,
    getBrochurePage,
    getLocationShortFormFields,
    getCountries,
    leadGenFullWidthData,
    checkInputFields,
    checkPulseUser,
    submitLead,
    processLeadSubmission,
    getSharedFullWidthRibbon,
    getFullWidthRibbonFormFields,
    validateWithZeroBounce
  }
}